import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * Toast with undo (T-0140).
 *
 * The web app had no toast primitive before this ticket (the
 * `@radix-ui/react-toast` dependency is installed but unused), so this
 * is a small one matching `.toast` in `docs/requirement-library/
 * prototype/selection.css`: an ink-900 pill bottom-right with the
 * message and up to two underlined actions (typically "View" and
 * "Undo"). One toast shows at a time; a new one replaces the old one.
 * It dismisses itself after 7 seconds, or when an action is clicked.
 *
 * T-0157 adds toasts that stay until dismissed (`durationMs: null`), for
 * something the person must not miss, such as an AI document import that
 * failed while they were elsewhere in the app. They stack above the
 * passing toast instead of replacing it, carry a Dismiss button, and are
 * announced as alerts. Giving one an `id` makes raising it again replace
 * it rather than add a second copy.
 *
 * Usage:
 *   <ToastProvider> ... </ToastProvider>          // once, near the root
 *   const { toast } = useToast();
 *   toast({ message: '3 requirements removed', actions: [{ label: 'Undo', onClick: undo }] });
 *   toast({ message: 'Import failed', tone: 'critical', durationMs: null, id: 'run-1' });
 *
 * The passing toast's region is `role="status"` (polite live region) so
 * the message is announced without stealing focus; actions are real
 * buttons.
 */

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  message: React.ReactNode;
  /** Up to two actions, for example View and Undo. */
  actions?: ReadonlyArray<ToastAction>;
  /**
   * Auto-dismiss delay in ms. Default 7000. `null` keeps the toast until
   * it is dismissed or one of its actions is chosen.
   */
  durationMs?: number | null;
  /** Identity of a toast that stays: raising the same id again replaces it. */
  id?: string;
  /** `critical` marks something that went wrong (a red bar on the left). */
  tone?: 'default' | 'critical';
}

interface ToastContextValue {
  /** Show a toast. Returns its id, for `dismiss(id)`. */
  toast: (options: ToastOptions) => string;
  /** Without an id: the passing toast. With one: that toast. */
  dismiss: (id?: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export const TOAST_DEFAULT_DURATION_MS = 7000;

type ShownToast = ToastOptions & { key: number; toastId: string };

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [current, setCurrent] = React.useState<ShownToast | null>(null);
  const [pinned, setPinned] = React.useState<ReadonlyArray<ShownToast>>([]);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const seq = React.useRef(0);

  const dismiss = React.useCallback((id?: string) => {
    if (id !== undefined) {
      setPinned((prev) => prev.filter((t) => t.toastId !== id));
      setCurrent((prev) => (prev?.toastId === id ? null : prev));
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setCurrent(null);
  }, []);

  const toast = React.useCallback((options: ToastOptions) => {
    seq.current += 1;
    const toastId = options.id ?? `toast-${seq.current}`;
    const shown: ShownToast = { ...options, key: seq.current, toastId };
    if (options.durationMs === null) {
      setPinned((prev) => {
        const at = prev.findIndex((t) => t.toastId === toastId);
        if (at === -1) return [...prev, shown];
        const next = [...prev];
        next[at] = shown;
        return next;
      });
      return toastId;
    }
    if (timer.current) clearTimeout(timer.current);
    setCurrent(shown);
    timer.current = setTimeout(() => {
      timer.current = null;
      setCurrent(null);
    }, options.durationMs ?? TOAST_DEFAULT_DURATION_MS);
    return toastId;
  }, []);

  React.useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const value = React.useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-6 right-6 z-[80] flex max-w-[calc(100vw-48px)] flex-col items-end gap-2">
        {pinned.map((t) => (
          <ToastCard
            key={t.key}
            toast={t}
            role="alert"
            onDismiss={() => dismiss(t.toastId)}
            dismissable
          />
        ))}
        <div role="status" aria-live="polite">
          {current ? (
            <ToastCard
              key={current.key}
              toast={current}
              onDismiss={() => dismiss(current.toastId)}
              dismissable={false}
            />
          ) : null}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({
  toast,
  role,
  onDismiss,
  dismissable,
}: {
  toast: ShownToast;
  role?: 'alert';
  onDismiss: () => void;
  dismissable: boolean;
}) {
  return (
    <div
      role={role}
      data-toast-id={toast.toastId}
      className={cn(
        'flex max-w-[480px] items-center gap-3.5 rounded-card bg-ink-900 px-3.5 py-2.5 text-body-sm text-paper-0 shadow-modal',
        toast.tone === 'critical' && 'shadow-[inset_3px_0_0_var(--posture-critical)]',
      )}
    >
      <span className="min-w-0">{toast.message}</span>
      {toast.actions?.slice(0, 2).map((a) => (
        <button
          key={a.label}
          type="button"
          onClick={() => {
            onDismiss();
            a.onClick();
          }}
          className="flex-none rounded-chip font-semibold text-accent-amber-100 underline underline-offset-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-500"
        >
          {a.label}
        </button>
      ))}
      {dismissable ? (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="-mr-1 flex h-6 w-6 flex-none items-center justify-center rounded-chip text-ink-300 hover:text-paper-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-500"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
